import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { EnvironmentBanner } from "@/components/ui/EnvironmentBanner";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createContext, useContext, useEffect, useRef, useState, lazy, Suspense } from "react";
import { clearOfflineCache } from "@/lib/offline-cache";
import { clearQueueSession } from "@/lib/offline-queue";
import { BrowserRouter as Router, Navigate, Route, Routes, useNavigate, useLocation } from "react-router-dom";
import { Layout } from "./components/layout/Layout";
import { PwaManager } from "@/components/pwa/PwaManager";

// Route-level code splitting: only the shell + the requested page are
// downloaded. Every page below used to sit in the initial bundle (one 3.4 MB
// chunk); this lets Rollup emit a small chunk per route instead.
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Inventory = lazy(() => import("./pages/Inventory"));
const Login = lazy(() => import("./pages/Login"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Orders = lazy(() => import("./pages/Orders"));
const WebsiteOrders = lazy(() => import("./pages/WebsiteOrders"));
const Creditors = lazy(() => import("./pages/Creditors"));
const Signup = lazy(() => import("./pages/Signup"));
const CreditCustomers = lazy(() => import("./pages/CreditCustomers"));
const Customers = lazy(() => import("./pages/Customers"));
const Suppliers = lazy(() => import("./pages/Suppliers"));
const CreditCustomerDetails = lazy(() => import("./pages/CreditCustomerDetails"));
const PurchaseAnalytics = lazy(() => import("./pages/PurchaseAnalytics"));
const Settings = lazy(() => import("./pages/Settings"));
const Pay = lazy(() => import("./pages/Pay"));
const AiAgents = lazy(() => import("./pages/AiAgents"));
const MobileTimberManagement = lazy(() => import("./pages/MobileTimberManagement"));
const TimberProductionPage = lazy(() => import("./pages/TimberProductionPage"));
const TimberAnalytics = lazy(() => import("./pages/TimberAnalytics"));
const FormulasPage = lazy(() => import("./pages/FormulasPage"));
const ProductionRunPage = lazy(() => import("./pages/ProductionRunPage"));
const QuickSalePage = lazy(() => import("./pages/QuickSalePage"));
const WastePage = lazy(() => import("./pages/WastePage"));
const AccountingPage = lazy(() => import("./pages/AccountingPage"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const SystemAdminDashboard = lazy(() => import("./pages/SystemAdminDashboard"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const GoogleAuthCallback = lazy(() => import("./pages/GoogleAuthCallback"));
const GoogleOnboarding = lazy(() => import("./pages/GoogleOnboarding"));
const PublicStorefront = lazy(() => import("./pages/public/PublicStorefront"));
const ReferralLanding = lazy(() => import("./pages/referrals/ReferralLanding"));
const ReferralJoin = lazy(() => import("./pages/referrals/ReferralJoin"));
const ReferralLogin = lazy(() => import("./pages/referrals/ReferralLogin"));
const ReferralTerms = lazy(() => import("./pages/referrals/ReferralTerms"));
const ReferralLayout = lazy(() => import("./pages/referrals/ReferralLayout"));
const ReferralDashboard = lazy(() => import("./pages/referrals/ReferralDashboard"));
const ReferralMyReferrals = lazy(() => import("./pages/referrals/ReferralMyReferrals"));
const ReferralRewards = lazy(() => import("./pages/referrals/ReferralRewards"));
const ReferralPayouts = lazy(() => import("./pages/referrals/ReferralPayouts"));
const ReferralSettings = lazy(() => import("./pages/referrals/ReferralSettings"));
const ReferralAdmin = lazy(() => import("./pages/referrals/ReferralAdmin"));
const Checkout = lazy(() => import("./pages/public/Checkout"));  const LandingPage = lazy(() => import("./pages/landing/LandingPage"));
  const PrivacyPage = lazy(() =>
    import("./pages/legal/LegalPages").then((m) => ({ default: m.PrivacyPage }))
  );
  const TermsPage = lazy(() =>
    import("./pages/legal/LegalPages").then((m) => ({ default: m.TermsPage }))
  );
import { UserProvider, useUser } from "./contexts/UserContext";
import { useCompanyPreferences, resolveStaffNav } from "./hooks/use-company-preferences";
import { Loader2 } from "lucide-react";
import AppErrorBoundary from "./components/ErrorBoundary";

/** Shown while a route's chunk is being fetched. */
const RouteFallback = () => (
  <div
    className="min-h-screen w-full flex items-center justify-center bg-background"
    role="status"
    aria-label="Loading"
  >
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error: any) => {
        // Never retry on 401 — token is expired/invalid, retrying just hammers the server
        if (error?.message?.includes('401') || error?.status === 401) return false;
        return failureCount < 2;
      },
      staleTime: 30 * 1000, // 30 seconds — reduce refetch frequency
    },
  },
});

// Create auth context
interface AuthContextType {
  isAuthenticated: boolean;
  setIsAuthenticated: (value: boolean) => void;
}

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  setIsAuthenticated: () => {},
});

export const useAuth = () => useContext(AuthContext);

// Maps route paths to their canonical path for matching
// e.g. /orders/sales → /orders/sales
function routeMatchesStaffNav(pathname: string, staffNav: string[]): boolean {
  return staffNav.some(allowed => {
    if (allowed === "/") return pathname === "/";
    return pathname === allowed || pathname.startsWith(allowed + "/");
  });
}

/**
 * Guards a route — if the user's granted pages (per-user list first, then the
 * general staff_nav) don't include the current path, redirect to their first
 * allowed page. Settings is never blocked so staff can review their access.
 */
function StaffGuard({ children }: { children: React.ReactNode }) {
  const { user, isAdmin, loading } = useUser();
  const { preferences } = useCompanyPreferences();
  const location = useLocation();
  const granted = resolveStaffNav(preferences, user, isAdmin);

  if (loading || granted === null) return <>{children}</>;
  if (location.pathname === "/settings") return <>{children}</>;

  if (routeMatchesStaffNav(location.pathname, granted)) return <>{children}</>;

  // Redirect to first allowed page (resolveStaffNav guarantees at least "/")
  return <Navigate to={granted[0] || "/"} replace />;
}

// Helper function to get the base path for different environments
const getBasePath = () => {
  const pathname = window.location.pathname;
  if (pathname.startsWith('/dev')) return '/dev';
  if (pathname.startsWith('/staging')) return '/staging';
  return '';
};

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const basePath = getBasePath();

  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('token');
      if (token) {
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
      }
    };

    checkAuth();

    const handleAuthChange = (event: CustomEvent) => {
      setIsAuthenticated(event.detail);
    };

    window.addEventListener('authStateChange', handleAuthChange as EventListener);
    return () => {
      window.removeEventListener('authStateChange', handleAuthChange as EventListener);
    };
  }, [basePath]);

  // The offline cache holds the customer directory — personal data at rest on
  // the device — so it is wiped on logout, and only on logout. A ref tracks the
  // previous value because the first render is always unauthenticated, and
  // clearing then would throw away a returning user's saved data on startup.
  //
  // The queue's stored session goes with it: the bearer token kept in IndexedDB
  // for background replay must not outlive the logout that ended it. Queued
  // records themselves stay on the device, waiting for the next sign-in.
  const wasAuthenticated = useRef(false);
  useEffect(() => {
    if (wasAuthenticated.current && !isAuthenticated) {
      void clearOfflineCache();
      void clearQueueSession();
    }
    wasAuthenticated.current = isAuthenticated;
  }, [isAuthenticated]);

  const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
    const token = localStorage.getItem('token');
    const location = useLocation();
    if (token && !isAuthenticated) {
      return <>{children}</>;
    }
    if (!isAuthenticated && !token) {
      // The public root URL is the marketing landing page — logged-out
      // visitors get the site instead of an immediate bounce to /login.
      // Deeper app paths still go to login and return here afterwards.
      if (location.pathname === "/") {
        return <LandingPage />;
      }
      // Remember the page the user asked for so login can return them there.
      return <Navigate to={`${basePath}/login`} replace state={{ from: location.pathname }} />;
    }
    return <>{children}</>;
  };

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <EnvironmentBanner />
        <Toaster />
        <Sonner />
        <AuthContext.Provider value={{ isAuthenticated, setIsAuthenticated }}>
          <UserProvider>
            <Router basename={basePath}>
              <AppErrorBoundary>
              <Suspense fallback={<RouteFallback />}>
              <Routes>
                {/* Auth Routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/auth/google" element={<GoogleAuthCallback />} />
                <Route path="/onboarding" element={<GoogleOnboarding />} />
                {/* Legal pages are public — reachable logged out from the footer. */}
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                
                {/* Public Storefront Routes - Must come before protected routes */}
                <Route 
                  path="/store/:storeSlug" 
                  element={<PublicStorefront />} 
                />
                <Route 
                  path="/store/:storeSlug/checkout" 
                  element={<Checkout />} 
                />

                {/* Referral programme: public pages + the referrer area */}
                <Route path="/referrals">
                  <Route index element={<ReferralLanding />} />
                  <Route path="join" element={<ReferralJoin />} />
                  <Route path="login" element={<ReferralLogin />} />
                  <Route path="terms" element={<ReferralTerms />} />
                  <Route element={<ReferralLayout />}>
                    <Route path="dashboard" element={<ReferralDashboard />} />
                    <Route path="my-referrals" element={<ReferralMyReferrals />} />
                    <Route path="rewards" element={<ReferralRewards />} />
                    <Route path="payouts" element={<ReferralPayouts />} />
                    <Route path="settings" element={<ReferralSettings />} />
                  </Route>
                </Route>

                {/* Protected Admin Routes */}
                <Route 
                  path="/" 
                  element={
                    <ProtectedRoute>
                      <Layout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<StaffGuard><Dashboard /></StaffGuard>} />
                  <Route path="inventory" element={<StaffGuard><Inventory /></StaffGuard>} />
                  <Route path="orders">
                    <Route index element={<StaffGuard><WebsiteOrders /></StaffGuard>} />
                    <Route path="purchases" element={<StaffGuard><Orders /></StaffGuard>} />
                    <Route path="sales" element={<StaffGuard><Orders /></StaffGuard>} />
                  </Route>
                  <Route path="creditors" element={<StaffGuard><Creditors /></StaffGuard>} />
                  <Route path="credit-customers" element={<StaffGuard><CreditCustomers /></StaffGuard>} />
                  <Route path="customers" element={<StaffGuard><Customers /></StaffGuard>} />
                  <Route path="suppliers" element={<StaffGuard><Suppliers /></StaffGuard>} />
                  <Route path="credit-customers/:id" element={<StaffGuard><CreditCustomerDetails /></StaffGuard>} />
                  <Route path="purchase-analytics" element={<StaffGuard><PurchaseAnalytics /></StaffGuard>} />
                  <Route path="timber" element={<StaffGuard><MobileTimberManagement /></StaffGuard>} />
                  <Route path="timber/production" element={<StaffGuard><TimberProductionPage /></StaffGuard>} />
                  <Route path="timber-analytics" element={<StaffGuard><TimberAnalytics /></StaffGuard>} />
                  <Route path="production/formulas" element={<StaffGuard><FormulasPage /></StaffGuard>} />
                  <Route path="production/run" element={<StaffGuard><ProductionRunPage /></StaffGuard>} />
                  <Route path="production/quick-sale" element={<StaffGuard><QuickSalePage /></StaffGuard>} />
                  <Route path="waste" element={<StaffGuard><WastePage /></StaffGuard>} />
                  <Route path="accounting" element={<AccountingPage />} />
                  <Route path="admin" element={<AdminDashboard />} />
                  <Route path="admin/referrals" element={<ReferralAdmin />} />
                  <Route path="system-admin" element={<SystemAdminDashboard />} />
                  <Route path="settings" element={<StaffGuard><Settings /></StaffGuard>} />
                  {/* Subscription payment — never staff-guarded: the expiry banner
                      sends every admin here to settle up. */}
                  <Route path="pay" element={<Pay />} />
                  <Route path="agents" element={<StaffGuard><AiAgents /></StaffGuard>} />
                </Route>
                
                <Route path="*" element={<NotFound />} />
              </Routes>
              </Suspense>
              </AppErrorBoundary>
              {/* PWA install, update and notification cards (never over
                  POS/checkout). PwaManager owns the corner so the install and
                  notification cards can never overlap. */}
              <PwaManager isAuthenticated={isAuthenticated} />
            </Router>
          </UserProvider>
        </AuthContext.Provider>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
